import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { X, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AccountSettingsModal({ user, onClose, onUpdate }) {
  const [formData, setFormData] = useState({
    name: user.name || '',
    bio: user.bio || '',
    postcode: user.postcode || '',
    experience_level: user.experience_level || 'beginner',
    preferred_distance: user.preferred_distance || 30,
    unit_preference: user.unit_preference || 'metric',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);

  const validatePostcode = (postcode) => {
    if (!postcode) return { valid: true }; // Optional
    const cleaned = postcode.replace(/\s/g, '').toUpperCase();
    if (cleaned.length < 2) {
      return { valid: false, message: 'Postcode must be at least 2 characters' };
    }
    const postcodeRegex = /^[A-Z]{1,2}[0-9]{1,2}[A-Z]?$/;
    const fullPostcodeRegex = /^[A-Z]{1,2}[0-9]{1,2}[A-Z]?[0-9][A-Z]{2}$/;
    if (!postcodeRegex.test(cleaned) && !fullPostcodeRegex.test(cleaned)) {
      return { valid: false, message: 'Invalid UK postcode format' };
    }
    return { valid: true, formatted: cleaned };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      // Validate postcode if provided
      if (formData.postcode) {
        const validation = validatePostcode(formData.postcode);
        if (!validation.valid) {
          toast.error(validation.message);
          setLoading(false);
          return;
        }
        formData.postcode = validation.formatted;
      }

      await axios.put('/users/profile', {
        name: formData.name,
        bio: formData.bio,
        postcode: formData.postcode,
        experience_level: formData.experience_level,
        preferred_distance: parseInt(formData.preferred_distance),
        unit_preference: formData.unit_preference,
      });
      
      toast.success('Profile updated successfully!');
      onUpdate(); // Refresh user data
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!formData.currentPassword || !formData.newPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (formData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await axios.put('/auth/change-password', {
        current_password: formData.currentPassword,
        new_password: formData.newPassword,
      });
      toast.success('Password changed successfully!');
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      await axios.delete('/users/account');
      toast.success('Account deleted successfully');
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      window.location.href = '/auth';
    } catch (error) {
      toast.error('Failed to delete account');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 border-b border-gray-800 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">Account Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Profile Information */}
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                />
              </div>
              <div>
                <Label htmlFor="bio">Bio</Label>
                <textarea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  placeholder="Tell others about yourself..."
                  className="w-full p-2 rounded-md bg-gray-800 border border-gray-700 text-white min-h-[100px]"
                />
              </div>
              <div>
                <Label htmlFor="postcode">Location (Postcode)</Label>
                <Input
                  id="postcode"
                  name="postcode"
                  value={formData.postcode}
                  onChange={handleChange}
                  placeholder="e.g., M28, SW1, or M28 1AA"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Used to find nearby riders and rides
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Riding Preferences */}
          <Card>
            <CardHeader>
              <CardTitle>Riding Preferences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="experience_level">Experience Level</Label>
                <select
                  id="experience_level"
                  name="experience_level"
                  value={formData.experience_level}
                  onChange={handleChange}
                  className="w-full p-2 rounded-md bg-gray-800 border border-gray-700 text-white"
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
              <div>
                <Label htmlFor="preferred_distance">Preferred Distance (km)</Label>
                <Input
                  id="preferred_distance"
                  name="preferred_distance"
                  type="number"
                  min="5"
                  max="200"
                  value={formData.preferred_distance}
                  onChange={handleChange}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Your typical ride distance
                </p>
              </div>
              <div>
                <Label htmlFor="unit_preference">Unit Preference</Label>
                <select
                  id="unit_preference"
                  name="unit_preference"
                  value={formData.unit_preference}
                  onChange={handleChange}
                  className="w-full p-2 rounded-md bg-gray-800 border border-gray-700 text-white"
                >
                  <option value="metric">Metric (km)</option>
                  <option value="imperial">Imperial (miles)</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  How distances are displayed throughout the app
                </p>
              </div>
            </CardContent>
          </Card>

          <Button
            onClick={handleSaveProfile}
            disabled={loading}
            className="w-full"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Saving...' : 'Save Profile Changes'}
          </Button>

          <Separator />

          {/* Change Password */}
          <Card>
            <CardHeader>
              <CardTitle>Change Password</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="currentPassword">Current Password</Label>
                <Input
                  id="currentPassword"
                  name="currentPassword"
                  type="password"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                />
              </div>
              <div>
                <Label htmlFor="newPassword">New Password</Label>
                <Input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  value={formData.newPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                />
              </div>
              <div>
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                />
              </div>
              <Button
                onClick={handleChangePassword}
                disabled={loading}
                variant="outline"
                className="w-full"
              >
                Change Password
              </Button>
            </CardContent>
          </Card>

          <Separator />

          {/* Delete Account */}
          <Card className="border-red-900/30">
            <CardHeader>
              <CardTitle className="text-red-500">Delete Account</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Once you delete your account, there is no going back. All your rides, 
                messages, and ratings will be permanently removed.
              </p>
              <Button
                variant="destructive"
                onClick={() => {
                  if (window.confirm('Are you absolutely sure? This action cannot be undone and all your data will be permanently deleted.')) {
                    handleDeleteAccount();
                  }
                }}
                className="w-full"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete My Account
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
